import { RouterRootState } from "connected-react-router";
import React, { FC, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import { useDialog } from "../../../components/dialog/DialogContext";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import TimeTriggeredActionModal from "../../../components/timeTriggeredAction/TimeTriggeredActionModal";
import TimeTriggeredActionTab from "../../../containers/time-triggered-actions-tab";
import { useLocale } from "../../../contexts/localeContext";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useTabCount } from "../../../hooks/useCategoryTab";
import { Button } from "../../../components/button/Button";
import plusIcon from "../../../images/Plus.svg";
import { Category } from "../../../models/Category";
import { TimeTriggeredAction } from "../../../models/TimeTriggeredAction";
import { storeTimeTriggeredAction } from "../../../services/timeTriggeredActionService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import { getListOfSiteIdsFromFilterWithKey } from "../../../utilities/tabUtils";

const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const TimeTriggeredActions: FC = () => {
    const [selected, setSelected] = useState(0);
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const { translate } = useLocale();
    const [showModal, setShowModal] = useState(false);
    const [currentItem, setCurrentItem] = useState<TimeTriggeredAction>({
        id: 0,
        name: '',
    })
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));
    const dialog = useDialog();

    const {updateTabCount} = useTabCount({
        tableName:"TimeTriggeredAction",
        pathToSite: "timePattern.site",
        categories,
        setCategories
    });

    const buildCategoriesData = async () => {
        try {
            setBusy(true);
            setCategories([{
                displayName: translate.byKey(TranslationKeys.timeTriggeredActions),
                heading: translate.byKey(TranslationKeys.timeTriggeredActions),
                value: "",
                id: 0,
                count: 0
            }]);

            setBusy(false);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    useEffect(() => {
        buildCategoriesData();
    }, []);

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent = [
                <TimeTriggeredActionTab
                    setBusy={setBusy}
                    categories={categories}
                    setCategories={setCategories}
                    openModal={setShowModal}
                    isModalOpen={showModal}
                    onFilterChange={onfilterChange}
                    setCurrentItem={setCurrentItem} />
            ];
            setTabContent(newTabContent);
        }
    }, [categories]);

    const onfilterChange = (filters: any) => {
        const siteIds = getListOfSiteIdsFromFilterWithKey(filters,"devAction.deviceAction.deviceImprox.site.id");
        updateTabCount(siteIds);
    }

    const onModalClose = () => {
        setCurrentItem({
            id: 0,
            name: '',
        })
        setShowModal(false)
    }

    const saveItem = (values) => {
        const timeTriggeredAction: TimeTriggeredAction = {
            id: values.id ?? 0,
            name: values.name,
            timePatternId: values.timePatternId,
            deviceActionId: values.deviceActionId
        }
        const isNew = values.id ? false : true;

        setBusy(true);
        storeTimeTriggeredAction(timeTriggeredAction, isNew)
            .then(async (value) => {
                toast.success(translate.byKey(TranslationKeys.timeTriggeredActionSaved));
                if (currentItem.id === 0) {
                    setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) + 1 }]);
                }
                onModalClose();
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return (
        <>
            <ActivityFrame busy={busy} className="flex flex-grow overflow-y-hidden">
                <div id="container-tabs" className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
                    <Breadcrumb
                        path={[
                            { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                            { value: "/access-control/hardware/time-triggered-actions", displayName: translate.byKey(TranslationKeys.timeTriggeredActions) }]}
                        onClick={(item) => history.push(item.value)}>
                    </Breadcrumb>
                    <Tabs
                        className="overflow-y-hidden"
                        tabs={getTabsListFromLabels(categories)}
                        onSelect={(i) => setSelected(i)}
                        selected={selected}
                        setSelectedTab={setSelected}
                        onAdd={() => { }}
                        add={false}
                        tabStyle={{}}>
                        {tabContent[selected]}
                        <div className="flex flex-col flex-grow overflow-y-hidden mb-viewport">
                            <div className="flex flex-row justify-end button-cluster">
                                <Restricted to={`add.${routeName}.element`}>
                                    <Button
                                        variant="submit"
                                        shape="rounded"
                                        id='addTimeTriggeredActionbtn'
                                        onClick={() => { setShowModal(true) }}>
                                        <img src={plusIcon} className="px-2 w-8 py-1" alt="" />
                                        <span className="mr-2">{translate.byKey(TranslationKeys.add)}</span>
                                    </Button>
                                </Restricted>
                            </div>
                        </div>
                    </Tabs>
                </div>
                <TimeTriggeredActionModal currentItem={currentItem} isOpen={showModal} onClose={onModalClose} saveTimeTriggeredAction={saveItem} />
            </ActivityFrame>
        </>
    );
};

export default TimeTriggeredActions;
